import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, ArrowRight, ArrowLeft } from "lucide-react";
import api, { formatDate } from "@/lib/api";
import { InterviewCard, LoadingBlock } from "@/components/site/Cards";

function useReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    function update() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(Math.max(scrollTop / total, 0), 1) : 0);
    }
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);
  return progress;
}

export default function InterviewDetail() {
  const { slug } = useParams();
  const [iv, setIv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const progress = useReadingProgress();

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const r = await api.get(`/interviews/${slug}`);
        setIv(r.data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <LoadingBlock />;
  if (notFound)
    return (
      <div className="max-w-editorial mx-auto px-6 md:px-10 py-32 text-center">
        <div className="eyebrow text-oc-coral mb-4">404</div>
        <h1 className="font-editorial text-4xl">Interview not found.</h1>
        <Link to="/interviews" className="mt-6 inline-flex font-ui text-sm font-bold uppercase tracking-widest border-b border-oc-blue text-oc-blue">
          Back to interviews
        </Link>
      </div>
    );
  if (!iv) return null;

  const sections = iv.body || [];
  const os = iv.operating_system || {};
  const osEntries = Object.entries(os).filter(([, v]) => v);

  return (
    <article data-testid="interview-detail" className="pb-24">
      {/* READING PROGRESS BAR */}
      <div
        aria-hidden="true"
        className="fixed top-0 left-0 right-0 h-1 z-[60] bg-oc-hairline/30 pointer-events-none"
      >
        <div
          data-testid="reading-progress"
          className="h-full bg-oc-coral"
          style={{ width: `${Math.round(progress * 100)}%`, transition: "width 80ms linear" }}
        />
      </div>

      {/* HERO */}
      <section className="border-b border-oc-hairline bg-oc-sand">
        <div className="max-w-editorial mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-12 md:pb-20">
          <Link to="/interviews" className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest text-oc-ink hover:text-oc-blue mb-8">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Interviews
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-8">
              <div className="eyebrow text-oc-coral mb-5">
                <span>Interview</span>
                {iv.industry_slug && <span className="text-oc-ink-muted"> · <Link to={`/industries/${iv.industry_slug}`} className="hover:text-oc-blue">{iv.industry?.name || iv.industry_slug}</Link></span>}
                {iv.reading_time && <span className="text-oc-ink-muted"> · {iv.reading_time} min read</span>}
              </div>
              <h1 className="font-editorial text-4xl sm:text-5xl md:text-6xl leading-[1.03] tracking-tight text-oc-ink">
                {iv.title}
              </h1>
              {iv.subtitle && (
                <p className="mt-6 font-editorial text-xl md:text-2xl text-oc-ink-soft leading-snug">
                  {iv.subtitle}
                </p>
              )}
              <div className="mt-8 pt-6 border-t border-oc-hairline-strong flex flex-wrap items-center gap-x-6 gap-y-3 font-ui text-sm">
                <div>
                  {iv.operator_slug ? (
                    <Link to={`/operators/${iv.operator_slug}`} className="font-bold text-oc-ink hover:text-oc-blue">
                      {iv.guest_name}
                    </Link>
                  ) : (
                    <span className="font-bold text-oc-ink">{iv.guest_name}</span>
                  )}
                  <span className="text-oc-ink-muted"> · {iv.guest_role}</span>
                </div>
                {iv.location && <div className="text-oc-ink-muted">{iv.location}</div>}
                {iv.published_at && <div className="text-oc-ink-muted">{formatDate(iv.published_at)}</div>}
              </div>
            </div>
            {iv.hero_image && (
              <div className="lg:col-span-4">
                <div className="relative aspect-[4/5] overflow-hidden bg-oc-sand-warm">
                  <img src={iv.hero_image} alt={iv.title} className="w-full h-full object-cover" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* BODY */}
      <div className="max-w-editorial mx-auto px-6 md:px-10 mt-16 md:mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* TOC */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-28">
              <div className="eyebrow text-oc-ink-muted mb-4">In this conversation</div>
              <nav className="space-y-1">
                {sections.map((s, i) => (
                  <a
                    key={i}
                    href={`#section-${i}`}
                    className="block py-2 pl-4 border-l border-oc-hairline text-sm font-ui text-oc-ink-muted hover:text-oc-blue hover:border-oc-coral transition-colors"
                  >
                    {s.heading || `Section ${i + 1}`}
                  </a>
                ))}
                {osEntries.length > 0 && (
                  <a
                    href="#operating-system"
                    className="block py-2 pl-4 border-l border-oc-hairline text-sm font-ui text-oc-coral font-semibold hover:text-oc-blue"
                  >
                    The Operating System
                  </a>
                )}
              </nav>
            </div>
          </aside>

          {/* Article */}
          <div className="lg:col-span-9 max-w-reading lg:max-w-none">
            {iv.executive_summary && (
              <div className="mb-14 pb-14 border-b border-oc-hairline">
                <div className="eyebrow text-oc-coral mb-4">Executive Summary</div>
                <p className="font-editorial text-xl md:text-2xl leading-snug text-oc-ink">
                  {iv.executive_summary}
                </p>
              </div>
            )}

            {iv.why_it_matters && (
              <div className="mb-14 pl-6 border-l-4 border-oc-blue">
                <div className="eyebrow text-oc-blue mb-3">Why this conversation matters</div>
                <p className="font-editorial text-lg text-oc-ink-soft leading-relaxed">{iv.why_it_matters}</p>
              </div>
            )}

            {iv.key_takeaways?.length > 0 && (
              <div className="mb-16 bg-oc-sand p-8 md:p-12">
                <div className="eyebrow text-oc-coral mb-6">Key Takeaways</div>
                <ol className="space-y-4 counter-reset list-none">
                  {iv.key_takeaways.map((t, i) => (
                    <li key={i} className="flex gap-4 font-editorial text-lg text-oc-ink leading-snug">
                      <span className="font-ui text-oc-coral font-bold text-sm mt-1.5 flex-shrink-0 w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="prose-editorial max-w-reading">
              {sections.map((s, i) => (
                <section key={i} id={`section-${i}`} className="scroll-mt-32">
                  {s.heading && (
                    <h2>{s.heading}</h2>
                  )}
                  <p>{s.content}</p>
                  {iv.pull_quotes?.[i] && (
                    <blockquote className="pull-quote">
                      "{iv.pull_quotes[i]}"
                    </blockquote>
                  )}
                </section>
              ))}
            </div>

            {/* THE OPERATING SYSTEM */}
            {osEntries.length > 0 && (
              <section id="operating-system" className="mt-20 pt-16 border-t border-oc-hairline-strong">
                <div className="eyebrow text-oc-coral mb-3">The Operating System</div>
                <h2 className="font-editorial text-3xl md:text-4xl leading-tight text-oc-ink">
                  {iv.guest_name}'s operating principles, distilled.
                </h2>
                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                  {osEntries.map(([k, v]) => (
                    <div key={k} className="border-t border-oc-hairline pt-5">
                      <div className="eyebrow text-oc-ink-muted mb-2">{k.replace(/_/g, " ")}</div>
                      <p className="font-editorial text-lg leading-snug text-oc-ink">{v}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* TOPIC TAGS */}
            {iv.topics?.length > 0 && (
              <div className="mt-16 pt-10 border-t border-oc-hairline">
                <div className="eyebrow text-oc-ink-muted mb-4">Topics in this conversation</div>
                <div className="flex flex-wrap gap-2">
                  {iv.topics.map((t) => (
                    <Link key={t} to={`/topics/${t}`} className="oc-chip">
                      {t.replace(/-/g, " ")}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTAs */}
      <section className="mt-24 bg-oc-ink text-white">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <div className="eyebrow text-oc-coral mb-4">Contribute</div>
              <h3 className="font-editorial text-3xl leading-tight">Have operating knowledge worth documenting?</h3>
              <Link to="/contribute" className="mt-6 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-coral pb-1 hover:text-oc-coral">
                Apply to be featured <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div>
              <div className="eyebrow text-oc-coral mb-4">Nominate</div>
              <h3 className="font-editorial text-3xl leading-tight">Know someone whose lessons deserve to be preserved?</h3>
              <Link to="/nominate" className="mt-6 inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest border-b border-oc-coral pb-1 hover:text-oc-coral">
                Nominate an operator <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED */}
      {iv.related?.length > 0 && (
        <section className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="eyebrow text-oc-coral mb-4">Continue reading</div>
          <h3 className="font-editorial text-3xl md:text-4xl text-oc-ink mb-12">Related conversations</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {iv.related.slice(0, 4).map((r) => (
              <InterviewCard key={r.slug} interview={r} />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
